/**
 * Service for syncing notifications from Firestore to local store
 */

import {
  collection,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNotificationsStore } from './notificationsStore';
import type { Notification } from '../types';

const COLLECTIONS = {
  USERS: 'users',
  NOTIFICATIONS: 'notifications',
} as const;

/**
 * Setup a real-time listener for notifications from Firestore
 * Returns unsubscribe function
 */
export const subscribeToNotifications = (userId: string) => {
  const notificationsRef = collection(
    db,
    COLLECTIONS.USERS,
    userId,
    COLLECTIONS.NOTIFICATIONS
  );

  const unsubscribe = onSnapshot(notificationsRef, (snapshot) => {
    const { addNotification } = useNotificationsStore.getState();

    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added') {
        const notification = change.doc.data() as Notification;
        addNotification(notification);
      }
    });
  });

  return unsubscribe;
};

/**
 * Mark a notification as read in Firestore
 */
export const markNotificationAsReadInFirestore = async (
  userId: string,
  notificationId: string
) => {
  const notificationRef = doc(
    db,
    COLLECTIONS.USERS,
    userId,
    COLLECTIONS.NOTIFICATIONS,
    notificationId
  );

  await updateDoc(notificationRef, {
    read: true,
    status: 'read',
  });
};

/**
 * Delete a notification from Firestore
 */
export const deleteNotificationFromFirestore = async (
  userId: string,
  notificationId: string
) => {
  const notificationRef = doc(
    db,
    COLLECTIONS.USERS,
    userId,
    COLLECTIONS.NOTIFICATIONS,
    notificationId
  );

  await deleteDoc(notificationRef);
};
